// Bottom-of-screen primary input. Quick chips log directly; typed text goes
// to the event parser (/api/parse) with an inline confirm step.

import { useState } from "react";
import { IconPlus, IconSparkle } from "./icons";
import { parseEvent, type DraftEvent } from "../lib/parseEvent";
import type { EventsApi, NewEvent } from "../lib/useEvents";
import { useT, useAmount } from "../lib/LocaleContext";
import type { Strings } from "../lib/i18n";

type Props = {
  babyId: string;
  events: EventsApi["events"];
  insert: EventsApi["insert"];
  update: EventsApi["update"];
  onAddNap: () => void;
  onOpenLog: () => void;
};

export function Composer({ babyId, events, insert, update, onAddNap, onOpenLog }: Props) {
  const t = useT();
  const amount = useAmount();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState<DraftEvent[] | null>(null);
  const [clarify, setClarify] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // State-aware quick chips. Their intent is already known, so they log
  // directly — no Gemini round-trip, instant at 2am.
  const liveSleep = events.find((e) => e.type === "sleep" && e.end_at === null);
  const caretakerLabel = localStorage.getItem("babyhelper:caretakerLabel") ?? undefined;
  const chipActions: { label: string; run: () => void }[] = [
    { label: t.composer.chipNursing(amount.fmt(4)), run: () => void insert({ type: "nurse", amount_oz: 4, caretaker_label: caretakerLabel }) },
    liveSleep
      ? { label: t.composer.chipWokeUp, run: () => void update(liveSleep.id, { end_at: new Date().toISOString() }) }
      : { label: t.composer.chipSleepingNow, run: () => void insert({ type: "sleep", sleep_kind: inferSleepKind(new Date()), caretaker_label: caretakerLabel }) },
    { label: t.logSheet.addNap, run: onAddNap },
  ];

  async function runParse(value: string) {
    setError(null);
    setBusy(true);
    try {
      const resp = await parseEvent(babyId, value);
      if (!resp.ok) {
        if (resp.reason === "rate-limited") setError(t.composer.errParseRateLimited);
        else if (resp.reason === "gemini-unavailable") setError(t.composer.errParseUnavailable);
        else setError(t.composer.errParseFailed);
        return;
      }
      setDrafts(resp.events);
      setClarify(resp.clarify ?? null);
    } finally {
      setBusy(false);
    }
  }

  async function submitText(value: string) {
    const v = value.trim();
    if (!v || busy) return;
    await runParse(v);
  }

  async function commitAll() {
    if (!drafts) return;
    const caretaker = localStorage.getItem("babyhelper:caretakerLabel") ?? undefined;
    for (const d of drafts) {
      if (d.kind === "wake") {
        const open = events.find((e) => e.type === "sleep" && e.end_at === null);
        if (open) await update(open.id, { end_at: d.at });
        continue;
      }
      const nev = draftToNewEvent(d);
      if (nev) await insert({ ...nev, source: "nl-parsed", caretaker_label: caretaker });
    }
    setDrafts(null);
    setClarify(null);
    setText("");
  }

  return (
    <div style={styles.wrap}>
      {drafts !== null && (
        <ConfirmCard
          t={t}
          fmtAmount={amount.fmt}
          drafts={drafts}
          clarify={clarify}
          onCommit={commitAll}
          onCancel={() => {
            setDrafts(null);
            setClarify(null);
          }}
        />
      )}
      {error && <div style={styles.error}>{error}</div>}
      <div style={styles.chips}>
        {chipActions.map((c) => (
          <button key={c.label} style={styles.chip} onClick={c.run}>
            {c.label}
          </button>
        ))}
      </div>
      <div style={styles.bar}>
        <button style={styles.iconBtn} onClick={onOpenLog} aria-label={t.composer.openLogAria}>
          <IconPlus size={20} />
        </button>
        <input
          style={styles.input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t.composer.inputPlaceholder}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !busy) submitText(text);
          }}
          disabled={busy}
        />
      </div>
    </div>
  );
}

function ConfirmCard({
  t,
  fmtAmount,
  drafts,
  clarify,
  onCommit,
  onCancel,
}: {
  t: Strings;
  fmtAmount: (oz: number) => string;
  drafts: DraftEvent[];
  clarify: string | null;
  onCommit: () => void;
  onCancel: () => void;
}) {
  return (
    <div style={styles.confirm}>
      <div style={styles.confirmHead}>
        <span style={styles.confirmIcon}>
          <IconSparkle size={14} />
        </span>
        <span style={styles.confirmLabel}>{clarify ? t.composer.confirmNeedMore : t.composer.confirmHeard}</span>
      </div>
      {clarify && <div style={styles.confirmBody}>{clarify}</div>}
      {drafts.length === 0 && !clarify && <div style={styles.confirmBody}>{t.composer.confirmNothing}</div>}
      {drafts.length > 0 && (
        <ul style={styles.draftList}>
          {drafts.map((d, i) => (
            <li key={i} style={styles.draftRow}>
              {summarizeDraft(t, fmtAmount, d)}
            </li>
          ))}
        </ul>
      )}
      <div style={styles.confirmActions}>
        <button style={styles.ghost} onClick={onCancel}>
          {t.composer.confirmCancel}
        </button>
        {drafts.length > 0 && (
          <button style={styles.primary} onClick={onCommit}>
            {t.composer.confirmLooksRight}
          </button>
        )}
      </div>
    </div>
  );
}

function summarizeDraft(t: Strings, fmtAmount: (oz: number) => string, d: DraftEvent): string {
  if (d.kind === "feed") {
    const m = d.method === "bottle" ? t.composer.draftMethodBottle : t.composer.draftMethodNursed;
    const amt = d.amountOz != null ? ` ${t.composer.draftOz(fmtAmount(d.amountOz))}` : "";
    const dur = d.durationMin != null ? ` ${t.composer.draftMin(d.durationMin)}` : "";
    const side = d.side
      ? ` ${d.side === "both" ? t.composer.draftBothSides : d.side === "L" ? t.composer.draftSideLeft : t.composer.draftSideRight}`
      : "";
    return t.composer.draftFeed(`${m}${amt}${dur}${side}`, fmtTime(d.at));
  }
  if (d.kind === "sleep") {
    if (d.live) return t.composer.draftSleepLive(fmtTime(d.at));
    return t.composer.draftSlept(fmtTime(d.at), d.endAt ? fmtTime(d.endAt) : t.composer.draftNow);
  }
  if (d.kind === "wake") return t.composer.draftWoke(fmtTime(d.at));
  if (d.kind === "diaper") return t.composer.draftDiaper(d.diaperKind, fmtTime(d.at));
  if (d.kind === "weight") return t.composer.draftWeight(d.lb, d.oz, fmtTime(d.at));
  if (d.kind === "note") return d.text;
  return JSON.stringify(d);
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function draftToNewEvent(d: DraftEvent): NewEvent | null {
  if (d.kind === "feed") {
    if (d.method === "bottle") {
      return { type: "feed", at: d.at, amount_oz: d.amountOz, duration_min: d.durationMin };
    }
    return { type: "nurse", at: d.at, duration_min: d.durationMin, side: d.side };
  }
  if (d.kind === "sleep") {
    return { type: "sleep", at: d.at, end_at: d.endAt ?? null, sleep_kind: inferSleepKind(new Date(d.at)) };
  }
  if (d.kind === "wake") {
    return null; // handled in commitAll — a wake closes the open sleep, not a new row
  }
  if (d.kind === "diaper") {
    return { type: "diaper", at: d.at, diaper_kind: d.diaperKind };
  }
  if (d.kind === "weight") {
    const grams = Math.round((d.lb * 16 + d.oz) * 28.3495);
    return { type: "weight", at: d.at, weight_grams: grams };
  }
  if (d.kind === "note") {
    return { type: "note", at: d.at, note: d.text };
  }
  return null;
}

function inferSleepKind(d: Date): "nap" | "night" {
  const h = d.getHours();
  return h >= 19 || h < 7 ? "night" : "nap";
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    padding: "8px 14px max(14px, env(safe-area-inset-bottom))",
    background: "linear-gradient(to top, var(--bh-bg) 70%, transparent)",
    zIndex: 40,
  },
  chips: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    paddingBottom: 8,
    scrollbarWidth: "none",
  },
  chip: {
    flexShrink: 0,
    appearance: "none",
    background: "var(--bh-surface)",
    color: "var(--bh-ink-2)",
    border: "1px solid var(--bh-divider)",
    padding: "8px 14px",
    borderRadius: 999,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  },
  bar: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    background: "var(--bh-surface)",
    border: "1px solid var(--bh-divider)",
    borderRadius: 999,
    padding: "8px 8px 8px 8px",
  },
  iconBtn: {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: 999,
    background: "var(--bh-surface-2)",
    border: "none",
    color: "var(--bh-ink-2)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  input: {
    flex: 1,
    minWidth: 0,
    appearance: "none",
    background: "transparent",
    border: "none",
    outline: "none",
    color: "var(--bh-ink)",
    fontSize: 16,
    fontFamily: "inherit",
    padding: "8px 4px",
  },
  confirm: {
    background: "var(--bh-surface)",
    border: "1px solid var(--bh-divider)",
    borderRadius: 20,
    padding: 14,
    marginBottom: 8,
  },
  confirmHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  confirmIcon: { color: "var(--bh-feed)", display: "inline-flex" },
  confirmLabel: { fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--bh-ink-3)" },
  confirmBody: { fontSize: 15, color: "var(--bh-ink-2)", marginBottom: 8 },
  draftList: {
    listStyle: "none",
    padding: 0,
    margin: "0 0 10px 0",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  draftRow: {
    fontSize: 15,
    color: "var(--bh-ink)",
    padding: "6px 10px",
    background: "var(--bh-surface-2)",
    borderRadius: 12,
  },
  confirmActions: { display: "flex", gap: 8, justifyContent: "flex-end" },
  primary: {
    background: "var(--bh-feed)",
    color: "oklch(0.2 0.04 70)",
    border: "none",
    padding: "10px 16px",
    fontSize: 15,
    fontWeight: 600,
    borderRadius: 12,
    cursor: "pointer",
  },
  ghost: {
    background: "var(--bh-surface-2)",
    color: "var(--bh-ink-2)",
    border: "none",
    padding: "10px 16px",
    fontSize: 15,
    fontWeight: 500,
    borderRadius: 12,
    cursor: "pointer",
  },
  error: {
    background: "var(--bh-attn-soft)",
    color: "var(--bh-attn)",
    padding: "10px 14px",
    borderRadius: 14,
    fontSize: 14,
    marginBottom: 8,
  },
};
